import React, {
  useMemo, useState, useRef
} from 'react';

import useVirtualizedElement from '@hooks/useVirtualizedElement/useVirtualizedElement';
import { getResizedImageUrl } from '@lib/image';
import AspectRatioBox from '@components/shared/AspectRatioBox/AspectRatioBox';
import SaveButton from '@components/shared/buttons/SaveButton/SaveButton';
import ExpandedSaveButton from '@components/shared/buttons/ExpandedSaveButton/ExpandedSaveButton';
import If from '@components/shared/conditional/If/If';
import pluralize from 'pluralize';
import Link from 'next/link';
import { LazyImage } from 'react-lazy-images';
import PlaceholderImage from '@components/shared/images/atoms/Image/PlaceholderImage';
import isBrowser from '@utils/browser/isBrowser';
import { IDesignerBoard } from '@components/pages/DesignerProfile/graphql/designer-boards.graphql';
import { IDesignerProfile } from '@components/pages/DesignerProfile/graphql/designer-profile-query.graphql';
import styles from './DesignerBoardCard.module.scss';
import FeatureIcon from './icons/feature.svg';
import FeaturedIcon from './icons/featured.svg';
import HiddenIcon from './icons/hidden.svg';
import VisibleIcon from './icons/visible.svg';

type DesignerBoardCardSize = 'small' | 'medium' | 'large';

interface IDesignerBoardCardProps {
  board: IDesignerBoard;
  designer: IDesignerProfile;
  analyticsListId?: string;
  className?: string;
  editing?: boolean;
  featured?: boolean;
  handleSetFeatured?: (board: IDesignerBoard) => void;
  handleSetHidden?: (board: IDesignerBoard, hidden: boolean) => void;
  saved?: boolean;
  onSave?: (typename: string, board: IDesignerBoard) => void;
  size?: DesignerBoardCardSize;
}

const DesignerBoardCard = ({
  board,
  designer,
  analyticsListId,
  editing = false,
  featured = false,
  handleSetFeatured = () => {
  },
  handleSetHidden = () => {
  },
  saved = false,
  onSave = () => {
  },
  size = 'small',
}: IDesignerBoardCardProps) => {
  const [hidden, setHidden] = useState(board.isHidden);
  const [isHovered, setIsHovered] = useState(false);

  const imageUrl = board.mainBoardAsset?.asset?.url;
  const containerRef = useRef(null);

  const isVisible = useVirtualizedElement(containerRef, 50);

  const aspectRatio = getAspectRatio(size);
  const imageDimensions = { x: 400, y: 400 * aspectRatio };
  const imageUrls = useMemo(() => {
    return {
      '400px': getResizedImageUrl({
        url: imageUrl,
        height: imageDimensions.y,
        width: imageDimensions.x,
        quality: 85,
      }),
      '800px': getResizedImageUrl({
        url: imageUrl,
        height: imageDimensions.y * 2,
        width: imageDimensions.x * 2,
        quality: 50,
      }),
      '1200px': getResizedImageUrl({
        url: imageUrl,
        height: imageDimensions.y * 3,
        width: imageDimensions.x * 3,
        quality: 50,
      }),
    };
  }, [imageUrl]);

  const toggleHidden = () => {
    setHidden(!hidden);
    handleSetHidden(board, !hidden);
  };

  const setFeatured = () => {
    handleSetFeatured(board);
  };

  const roomType = board.room?.roomType?.type || '';
  const altText = `${board.subStyles.map((ss) => ss.name).join(', ')} ${roomType} Design by Havenly Interior Designer ${designer.firstName}`.trim();

  const boardLink = getLink(board, analyticsListId);
  return (
    <AspectRatioBox aspectRatio={aspectRatio} ref={containerRef}>
      {isVisible && (
        <figure
          className={styles.Container}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Link href={ boardLink } passHref>
            <a href={ boardLink }>
              <LazyImage
                loadEagerly={!isBrowser()}
                actual={
                ({ imageProps }) => (
                  <img
                    {...imageProps}
                    alt={altText}
                    className={ `${styles.Image} ${styles.isLoaded}` }
                  />
                )
              }
                placeholder={
                ({ imageProps, ref }) => (
                  <PlaceholderImage {...imageProps} ref={ref} />
                )
              }
                src={imageUrls['800px']}
                sizes={ getImgSizes(size) }
                srcSet={ getImgSrcSet(size, imageUrls) }
              />
            </a>
          </Link>
          <If condition={editing}>
            <div className={`${styles.EditBar} ${styles[size.toUpperCase()]}`}>
              <If condition={hidden}>
                <button
                  type="button"
                  className={styles.Hide}
                  onClick={toggleHidden}
                >
                  <span
                    className={styles.HideIcon}
                  >
                    <HiddenIcon />
                  </span>
                  <span className={styles.EditText}>
                    Unhide
                  </span>
                </button>
              </If>
              <If condition={!hidden}>
                <If condition={featured}>
                  <span className={styles.Feature}>
                    <span
                      className={styles.FeatureIcon}
                    >
                      <FeaturedIcon />
                    </span>
                    <span className={styles.EditText}>
                      Featured
                    </span>
                  </span>
                </If>
                <If condition={!featured}>
                  <button
                    type="button"
                    className={styles.Feature}
                    onClick={setFeatured}
                  >
                    <span
                      className={styles.FeatureIcon}
                    >
                      <FeatureIcon />
                    </span>
                    <span className={styles.EditText}>
                      Feature
                    </span>
                  </button>
                  <button
                    type="button"
                    className={styles.Hide}
                    onClick={toggleHidden}
                  >
                    <span
                      className={styles.HideIcon}
                    >
                      <VisibleIcon />
                    </span>
                    <span className={styles.EditText}>
                      Hide
                    </span>
                  </button>
                </If>
              </If>

              <a
                className={styles.ViewBB}
                href={`/board-builder?board=${board.id}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                View in BB
              </a>
            </div>
          </If>
          <div
            className={`
            ${styles.Overlay}
            ${editing && styles.Editing}
            ${hidden && styles.Hidden}`}
          />
          <BoardCaption hidden={hidden} board={board} size={size} />
          <If condition={!editing}>
            {
              isHovered ? (
                <ExpandedSaveButton
                  className={styles.SaveButton}
                  onClick={() => onSave('BoardModel', board)}
                  saved={saved}
                />
              ) : (
                <SaveButton
                  className={styles.SaveButton}
                  onClick={() => onSave('BoardModel', board)}
                  saved={saved}
                  overlay
                  hovered={isHovered}
                />
              )
            }
          </If>
        </figure>
      )}
    </AspectRatioBox>
  );
};

export default DesignerBoardCard;

function BoardCaption({
  board,
  size,
  hidden
}: {
  board: IDesignerBoard;
  size: DesignerBoardCardSize;
  hidden: boolean;
}) {
  if (hidden) {
    return (
      <figcaption className={ styles.HiddenOverlayText }>
        <strong>
          This design is not visible to your clients
        </strong>
      </figcaption>
    );
  }

  return (
    <figcaption className={ styles.OverlayText }>
      <strong className={ size !== 'small' ? styles.AlwaysVisible : '' }>
        {board.productCount} {pluralize('Product', board.productCount || 1)}
      </strong>
    </figcaption>
  );
}

function getLink(board: IDesignerBoard, analyticsListId: string | undefined) {
  const analyticsLink = analyticsListId ? `?list=${analyticsListId}` : '';
  return `${board.publicUrl}${analyticsLink}`;
}

function getImgSizes(size = 'small') {
  switch (size) {
    case 'large': {
      return `
        (max-width: 600px) 800px,
        1200px
      `;
    }
    default: {
      return `
        (max-width: 600px) 400px,
        800px
      `;
    }
  }
}

function getImgSrcSet(size: DesignerBoardCardSize = 'small', imageUrls: Record<string, string>) {
  switch (size) {
    case 'large': {
      return `
        ${imageUrls['800px']} 800w,
        ${imageUrls['1200px']} 1200w
      `;
    }
    default: {
      return `
        ${imageUrls['400px']} 400w,
        ${imageUrls['800px']} 800w
      `;
    }
  }
}

function getAspectRatio(size: DesignerBoardCardSize = 'small') {
  switch (size) {
    case 'small': {
      return 1;
    }
    case 'medium': {
      return 1.02; // fudging it slightly to fit the templates
    }
    case 'large': {
      return 0.67;
    }
    default: {
      return 1;
    }
  }
}
